import { useGetMany, useListContext, useNotify } from "ra-core";
import { Route } from "lucide-react";
import { Button } from "@/components/ui/button";

import type { Prospect } from "../types";

// Google Maps web directions support at most 10 stops per route.
export const MAX_ROUTE_STOPS = 10;

export const prospectFullAddress = (prospect: Prospect): string | null => {
  if (!prospect.property_address) return null;
  return [
    prospect.property_address,
    prospect.property_city,
    prospect.property_state,
    prospect.property_zip,
  ]
    .filter(Boolean)
    .join(", ");
};

export const buildDrivingRouteUrl = (addresses: string[]): string =>
  `https://www.google.com/maps/dir/${addresses
    .map((address) => encodeURIComponent(address))
    .join("/")}`;

/**
 * Bulk action for the List (prospects) view: open a Google Maps driving
 * route through every checkbox-selected property, in selection order.
 */
export const ProspectBulkDrivingRoute = () => {
  const { selectedIds = [] } = useListContext<Prospect>();
  const notify = useNotify();
  const { data: prospects = [], isPending } = useGetMany<Prospect>(
    "prospects",
    { ids: selectedIds },
    { enabled: selectedIds.length > 0 },
  );

  const openRoute = () => {
    const byId = new Map(prospects.map((prospect) => [prospect.id, prospect]));
    const addresses = selectedIds
      .map((id) => byId.get(id))
      .filter((prospect): prospect is Prospect => Boolean(prospect))
      .map(prospectFullAddress)
      .filter((address): address is string => Boolean(address));
    const uniqueAddresses = [...new Set(addresses)];

    if (!uniqueAddresses.length) {
      notify("None of the selected prospects have a property address", {
        type: "warning",
      });
      return;
    }

    const skipped = selectedIds.length - addresses.length;
    if (uniqueAddresses.length > MAX_ROUTE_STOPS) {
      notify(
        `Google Maps allows ${MAX_ROUTE_STOPS} stops per route — routing the first ${MAX_ROUTE_STOPS}`,
        { type: "info" },
      );
    } else if (skipped > 0) {
      notify(`Skipped ${skipped} selected without a property address`, {
        type: "info",
      });
    }

    const stops = uniqueAddresses.slice(0, MAX_ROUTE_STOPS);
    window.open(buildDrivingRouteUrl(stops), "_blank", "noopener");
  };

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isPending}
      onClick={openRoute}
      title="Open a Google Maps driving route through the selected properties"
    >
      <Route className="size-4 mr-1" /> Driving route
    </Button>
  );
};
